import { type SourceRef } from '@aura/shared';
import { ApiError, type ChatApi } from './api.js';
import * as icon from './icons.js';
import { escapeHtml, renderMarkdown } from './markdown.js';
import styles from './styles.css?inline';

const OPEN_STORAGE = 'aura.open';
const TEASER_STORAGE = 'aura.teaser';
/** La burbuja de bienvenida aparece un rato después de cargar la página y se va sola. */
const TEASER_DELAY_MS = 3000;
const TEASER_VISIBLE_MS = 8000;
/** Lo que dura la animación de cierre del panel (`.panel.closing` en styles.css). */
const CLOSE_MS = 180;
/** Marca dónde va el cursor mientras llega el texto (un carácter de uso privado de Unicode). */
const CARET = String.fromCharCode(0xe000);

const TEMPLATE = `
  <section class="panel" role="dialog" aria-modal="false" aria-labelledby="aura-title" hidden>
    <header>
      <span class="avatar">${icon.sparkle(20)}</span>
      <div class="names">
        <h2 class="title" id="aura-title">Asistente</h2>
        <p class="subtitle"></p>
      </div>
      <button type="button" class="icon-button new" aria-label="Nueva conversación" title="Nueva conversación">${icon.newChat()}</button>
      <button type="button" class="icon-button close" aria-label="Cerrar asistente" title="Cerrar">${icon.close()}</button>
    </header>
    <div class="messages" aria-live="polite">
      <form class="key-form" hidden>
        <span class="avatar">${icon.sparkle(30)}</span>
        <strong class="key-title">Ingresá la clave de acceso</strong>
        <p>Te la pasa el equipo que administra el asistente.</p>
        <label class="sr-only" for="aura-key">Clave de acceso</label>
        <input id="aura-key" type="password" autocomplete="current-password" placeholder="Clave de acceso" required>
        <p class="key-error" role="alert" hidden></p>
        <button type="submit">Entrar</button>
      </form>
      <div class="empty">
        <span class="avatar">${icon.sparkle(30)}</span>
        <strong class="greeting">¿En qué te ayudo?</strong>
        Preguntá cómo hacer algo en el sistema. Respondo con la documentación y te muestro de dónde lo saqué.
        <div class="chips" hidden></div>
      </div>
    </div>
    <button type="button" class="scroll-down" aria-label="Ir al final de la conversación" hidden>${icon.arrowDown()}</button>
    <form class="composer">
      <label class="sr-only" for="aura-question">Tu pregunta</label>
      <div class="field">
        <textarea id="aura-question" rows="1" maxlength="2000" placeholder="Escribí tu pregunta…"></textarea>
        <button type="submit" class="send" aria-label="Enviar">${icon.send()}</button>
      </div>
      <p class="hint">Responde con la documentación del sistema y puede equivocarse.</p>
    </form>
  </section>
  <div class="teaser" role="status" hidden>
    <button type="button" class="teaser-text"></button>
    <button type="button" class="teaser-close" aria-label="Cerrar">${icon.close(14)}</button>
  </div>
  <button type="button" class="launcher" aria-label="Abrir asistente" aria-expanded="false">
    <span class="icon icon-open">${icon.sparkle(26)}</span>
    <span class="icon icon-close">${icon.close(24)}</span>
  </button>
`;

function session(key: string, value?: string | null): string | null {
  try {
    if (value === undefined) return sessionStorage.getItem(key);
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* sin almacenamiento: no se recuerda */
  }
  return null;
}

/** Sin `matchMedia` (tests) o con movimiento reducido, las animaciones no se esperan. */
const reducedMotion = () =>
  typeof matchMedia !== 'function' || matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Errores que no se arreglan reintentando la misma pregunta. */
const FINAL_ERRORS = new Set([
  'RATE_LIMITED',
  'CHAT_DISABLED',
  'ACCESS_KEY_INVALID',
  'NO_SESSION',
  'TOKEN_INVALID',
]);

/** En la página de chat, el usuario escribe la clave compartida (`index.ts` la guarda). */
export interface AccessKeyPrompt {
  /** Todavía no hay clave: se pide antes de la primera pregunta. */
  needed: boolean;
  submit(key: string): void;
  /** La API la rechazó: se olvida y se vuelve a pedir. */
  rejected(): void;
}

export interface AssistantOptions {
  /** Nombre de la asistente ("Laura"): título del panel y saludo. */
  assistantName: string;
  /** Debajo del nombre, ej. "Asistente de Lupa". */
  subtitle: string;
  primaryColor?: string;
  /** La burbuja de bienvenida (una vez por sesión). Por defecto, sí. */
  teaser?: boolean;
  /** `page`: siempre abierto y ocupando la página (sin botón, burbuja ni cerrar). */
  mode?: 'floating' | 'page';
  /** Fuentes debajo de cada respuesta y citas en el texto. Por defecto, no (lo decide el sistema). */
  showSources?: boolean;
  accessKey?: AccessKeyPrompt;
}

/**
 * `<aura-assistant>`: botón flotante + panel de chat, con los estilos aislados en el Shadow DOM.
 * Lo crea `index.ts` con la config del `<script>`; `setup()` lo conecta a la API.
 */
export class AuraAssistant extends HTMLElement {
  private api?: ChatApi;
  private readonly root: ShadowRoot;
  private readonly els: {
    panel: HTMLElement;
    title: HTMLElement;
    subtitle: HTMLElement;
    messages: HTMLElement;
    empty: HTMLElement;
    chips: HTMLElement;
    scrollDown: HTMLButtonElement;
    form: HTMLFormElement;
    question: HTMLTextAreaElement;
    send: HTMLButtonElement;
    teaser: HTMLElement;
    launcher: HTMLButtonElement;
    keyForm: HTMLFormElement;
    keyInput: HTMLInputElement;
    keyError: HTMLElement;
  };
  private page = false;
  private showSources = false;
  private keyPrompt?: AccessKeyPrompt;
  /** La pregunta que quedó esperando la clave. */
  private pending?: string;
  private conversationId: string | undefined;
  private busy = false;
  private abort?: AbortController;
  private suggestionsLoaded = false;
  private closeTimer?: ReturnType<typeof setTimeout>;
  private teaserTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
    this.root.innerHTML = `<style>${styles}</style>${TEMPLATE}`;
    const $ = <T extends Element>(selector: string) => this.root.querySelector<T>(selector)!;
    this.els = {
      panel: $('.panel'),
      title: $('.title'),
      subtitle: $('.subtitle'),
      messages: $('.messages'),
      empty: $('.empty'),
      chips: $('.chips'),
      scrollDown: $('.scroll-down'),
      form: $('.composer'),
      question: $('textarea'),
      send: $('.send'),
      teaser: $('.teaser'),
      launcher: $('.launcher'),
      keyForm: $('.key-form'),
      keyInput: $('#aura-key'),
      keyError: $('.key-error'),
    };
    this.bind();
  }

  setup(api: ChatApi, opts: AssistantOptions): void {
    this.api = api;
    const name = opts.assistantName;
    this.els.title.textContent = name;
    this.els.subtitle.textContent = opts.subtitle;
    this.els.panel.setAttribute('aria-label', `${name} · ${opts.subtitle}`);
    this.root.querySelector('.greeting')!.textContent = `Hola, soy ${name}. ¿En qué te ayudo?`;
    this.root.querySelector('.teaser-text')!.innerHTML =
      `¿Necesitás ayuda? Preguntale a <strong>${escapeHtml(name)}</strong>`;
    if (opts.primaryColor) this.style.setProperty('--aura-primary', opts.primaryColor);
    this.keyPrompt = opts.accessKey;
    this.showSources = opts.showSources ?? false;
    if (opts.mode === 'page') {
      this.page = true;
      this.classList.add('page');
      this.els.panel.setAttribute('role', 'main');
      if (this.keyPrompt?.needed) this.lock();
      this.open();
      return;
    }
    if (session(OPEN_STORAGE) === '1') this.open(false);
    else if (opts.teaser !== false) this.scheduleTeaser();
  }

  /** Página de chat sin clave: el formulario de la clave en lugar de la conversación. */
  private lock(error?: string): void {
    const { panel, keyForm, keyInput, keyError } = this.els;
    panel.classList.add('locked');
    keyForm.hidden = false;
    keyError.hidden = !error;
    keyError.textContent = error ?? '';
    keyInput.value = '';
    keyInput.focus();
  }

  /** Prueba la clave pidiendo las sugerencias (piden clave): si no sirve, se avisa ahí mismo. */
  private async unlock(): Promise<void> {
    const submit = this.els.keyForm.querySelector<HTMLButtonElement>('button')!;
    submit.disabled = true;
    try {
      this.showSuggestions(await this.api!.suggestions());
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ACCESS_KEY_INVALID') {
        this.keyPrompt?.rejected();
        this.lock('La clave no es correcta.');
        return;
      }
    } finally {
      submit.disabled = false;
    }
    this.els.panel.classList.remove('locked');
    this.els.keyForm.hidden = true;
    this.els.question.focus();
    const pending = this.pending;
    this.pending = undefined;
    if (pending) void this.ask(pending);
  }

  open(focus = true): void {
    clearTimeout(this.closeTimer);
    this.hideTeaser();
    this.els.panel.classList.remove('closing');
    this.els.panel.hidden = false;
    this.classList.add('open');
    this.els.launcher.setAttribute('aria-expanded', 'true');
    this.els.launcher.setAttribute('aria-label', 'Cerrar asistente');
    session(OPEN_STORAGE, '1');
    // Con el formulario de la clave a la vista, el foco y las sugerencias esperan a la clave.
    if (this.els.panel.classList.contains('locked')) return;
    if (focus) this.els.question.focus();
    this.scrollToBottom();
    void this.loadSuggestions();
  }

  close(): void {
    // La página de chat no se cierra: es la página.
    if (this.page || this.els.panel.hidden) return;
    this.classList.remove('open');
    this.els.launcher.setAttribute('aria-expanded', 'false');
    this.els.launcher.setAttribute('aria-label', 'Abrir asistente');
    session(OPEN_STORAGE, null);
    this.els.launcher.focus();
    const hide = () => {
      this.els.panel.hidden = true;
      this.els.panel.classList.remove('closing');
    };
    if (reducedMotion()) return hide();
    this.els.panel.classList.add('closing');
    this.closeTimer = setTimeout(hide, CLOSE_MS);
  }

  private bind(): void {
    const { launcher, panel, form, question, messages, scrollDown, teaser } = this.els;
    launcher.addEventListener('click', () =>
      this.classList.contains('open') ? this.close() : this.open(),
    );
    this.root.querySelector('.close')!.addEventListener('click', () => this.close());
    this.root.querySelector('.new')!.addEventListener('click', () => this.reset());
    panel.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.close();
      }
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const text = question.value.trim();
      if (!text || this.busy) return;
      question.value = '';
      this.autosize();
      void this.ask(text);
    });
    question.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        form.requestSubmit();
      }
    });
    question.addEventListener('input', () => this.autosize());
    messages.addEventListener('scroll', () => {
      scrollDown.hidden = this.isNearBottom();
    });
    scrollDown.addEventListener('click', () => {
      messages.scrollTo?.({ top: messages.scrollHeight, behavior: 'smooth' });
      scrollDown.hidden = true;
    });
    teaser.querySelector('.teaser-text')!.addEventListener('click', () => this.open());
    teaser.querySelector('.teaser-close')!.addEventListener('click', () => this.hideTeaser());
    this.els.keyForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const key = this.els.keyInput.value.trim();
      if (!key || !this.keyPrompt) return;
      this.keyPrompt.submit(key);
      void this.unlock();
    });
  }

  /** Una vez por sesión, si el usuario no abrió el panel, una burbuja junto al botón. */
  private scheduleTeaser(): void {
    if (session(TEASER_STORAGE)) return;
    this.teaserTimer = setTimeout(() => {
      if (this.classList.contains('open')) return;
      session(TEASER_STORAGE, '1');
      this.els.teaser.hidden = false;
      this.els.launcher.classList.add('attention');
      this.teaserTimer = setTimeout(() => this.hideTeaser(), TEASER_VISIBLE_MS);
    }, TEASER_DELAY_MS);
  }

  private hideTeaser(): void {
    clearTimeout(this.teaserTimer);
    const { teaser, launcher } = this.els;
    launcher.classList.remove('attention');
    if (teaser.hidden) return;
    if (reducedMotion()) {
      teaser.hidden = true;
      return;
    }
    teaser.classList.add('leaving');
    setTimeout(() => {
      teaser.hidden = true;
      teaser.classList.remove('leaving');
    }, 200);
  }

  /** Las preguntas sugeridas se piden la primera vez que se abre el panel. */
  private async loadSuggestions(): Promise<void> {
    if (this.suggestionsLoaded || !this.api) return;
    this.suggestionsLoaded = true;
    // Una clave rechazada se descubre al preguntar; acá alcanza con no mostrar sugerencias.
    this.showSuggestions(await this.api.suggestions().catch(() => []));
  }

  private showSuggestions(questions: string[]): void {
    this.suggestionsLoaded = true;
    const { chips } = this.els;
    chips.replaceChildren();
    for (const q of questions) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'chip';
      chip.textContent = q;
      chip.addEventListener('click', () => {
        if (!this.busy) void this.ask(q);
      });
      chips.append(chip);
    }
    chips.hidden = questions.length === 0;
  }

  private reset(): void {
    this.abort?.abort();
    this.conversationId = undefined;
    for (const el of [...this.els.messages.children]) if (el !== this.els.empty) el.remove();
    this.els.empty.hidden = false;
    this.els.scrollDown.hidden = true;
    this.els.question.focus();
  }

  private async ask(question: string): Promise<void> {
    if (!this.api) return;
    this.setBusy(true);
    this.animateSend();
    const userRow = this.addUser(question);
    const typing = this.addAssistantRow(
      'msg assistant typing',
      // Solo los puntos; el texto queda para los lectores de pantalla.
      '<span class="dots" aria-hidden="true"><i></i><i></i><i></i></span><span class="sr-only">Buscando la respuesta…</span>',
    );
    const abort = (this.abort = new AbortController());
    let answer: ReturnType<AuraAssistant['addAnswer']> | undefined;
    let sources: SourceRef[] = [];
    try {
      await this.api.ask(
        { question, conversationId: this.conversationId },
        (e) => {
          if (e.event === 'meta') this.conversationId = e.data.conversationId;
          else if (e.event === 'delta') {
            if (!answer) {
              typing.remove();
              answer = this.addAnswer();
            }
            answer.addText(e.data.text);
          } else if (e.event === 'sources') sources = e.data;
          else if (e.event === 'done') answer?.finish(sources, e.data.answered);
        },
        abort.signal,
      );
    } catch (err) {
      typing.remove();
      answer?.row.remove();
      if (abort.signal.aborted) return; // "Nueva conversación" en el medio.
      const error =
        err instanceof ApiError ? err : new ApiError('UNKNOWN', 'Algo salió mal. Probá de nuevo.');
      if (error.code === 'ACCESS_KEY_INVALID' && this.keyPrompt) {
        // Página de chat: se pide la clave de nuevo y la pregunta sale apenas la escriban.
        this.keyPrompt.rejected();
        userRow.remove();
        this.pending = question;
        this.lock('La clave no es correcta.');
      } else if (error.code === 'CONVERSATION_NOT_FOUND') {
        this.conversationId = undefined;
        userRow.remove();
        this.addError('Se reinició la conversación.', () => void this.ask(question));
      } else if (FINAL_ERRORS.has(error.code)) {
        this.addError(error.message);
      } else {
        this.addError(error.message, () => {
          userRow.remove();
          void this.ask(question);
        });
      }
    } finally {
      if (this.abort === abort) {
        this.abort = undefined;
        this.setBusy(false);
      }
    }
  }

  private addMessage(row: HTMLElement): HTMLElement {
    this.els.empty.hidden = true;
    this.els.messages.append(row);
    this.scrollToBottom();
    return row;
  }

  private addUser(text: string): HTMLElement {
    const row = document.createElement('div');
    row.className = 'msg user';
    row.innerHTML = `<div class="bubble">${escapeHtml(text)}</div>`;
    return this.addMessage(row);
  }

  /** Fila del asistente: su avatar y la burbuja. */
  private addAssistantRow(className: string, html: string): HTMLElement {
    const row = document.createElement('div');
    row.className = className;
    row.innerHTML = `<span class="avatar" aria-hidden="true">${icon.sparkle(15)}</span><div class="bubble">${html}</div>`;
    return this.addMessage(row);
  }

  /** Burbuja del asistente que se completa a medida que llega el texto. */
  private addAnswer() {
    const row = this.addAssistantRow('msg assistant', '<div class="body"></div>');
    const bubble = row.querySelector<HTMLElement>('.bubble')!;
    const body = bubble.querySelector<HTMLElement>('.body')!;
    let text = '';
    let scheduled = false;
    const render = (streaming: boolean) => {
      scheduled = false;
      // Si el usuario subió para leer, no se lo baja a la fuerza.
      const atBottom = this.isNearBottom();
      const html = renderMarkdown(streaming ? text + CARET : text, {
        citations: this.showSources,
      });
      body.innerHTML = streaming ? html.replace(CARET, '<span class="caret"></span>') : html;
      if (atBottom) this.scrollToBottom();
      else this.els.scrollDown.hidden = false;
    };
    return {
      row,
      addText(delta: string) {
        text += delta;
        if (!scheduled) {
          scheduled = true;
          requestAnimationFrame(() => render(true));
        }
      },
      finish: (sources: SourceRef[], answered: boolean) => {
        render(false);
        if (!answered) row.classList.add('no-info');
        if (this.showSources && sources.length) {
          const items = sources
            .map((s) => {
              const section = s.sectionPath ? ` › ${s.sectionPath.split(' > ').join(' › ')}` : '';
              return `<li>${escapeHtml(s.documentTitle + section)}</li>`;
            })
            .join('');
          bubble.insertAdjacentHTML(
            'beforeend',
            `<div class="sources"><div class="label">Fuentes</div><ol>${items}</ol></div>`,
          );
        }
        bubble.append(this.copyButton(body));
        if (this.isNearBottom(120)) this.scrollToBottom();
      },
    };
  }

  /** "Copiar" la respuesta como texto (sin las citas). */
  private copyButton(body: HTMLElement): HTMLElement {
    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.innerHTML = `<button type="button" class="copy" aria-label="Copiar la respuesta">${icon.copy()}<span>Copiar</span></button>`;
    const button = actions.querySelector<HTMLButtonElement>('.copy')!;
    button.addEventListener('click', () => {
      // Las citas son pseudo-elementos (`.cite::after`): no forman parte del texto.
      const text = (body.innerText || body.textContent || '').trim();
      void navigator.clipboard?.writeText(text).then(() => {
        button.classList.add('done');
        button.innerHTML = `${icon.check()}<span>Copiado</span>`;
        setTimeout(() => {
          button.classList.remove('done');
          button.innerHTML = `${icon.copy()}<span>Copiar</span>`;
        }, 1500);
      });
    });
    return actions;
  }

  private addError(message: string, retry?: () => void): void {
    const row = document.createElement('div');
    row.className = 'error';
    row.setAttribute('role', 'alert');
    row.innerHTML = `${icon.alert()}<div class="error-body"></div>`;
    const content = row.querySelector('.error-body')!;
    content.textContent = message;
    if (retry) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = 'Reintentar';
      button.addEventListener('click', () => {
        row.remove();
        retry();
      });
      content.append(button);
    }
    this.addMessage(row);
  }

  private animateSend(): void {
    const { send } = this.els;
    send.classList.remove('sent');
    void send.offsetWidth; // reinicia la animación si se manda dos veces seguidas
    send.classList.add('sent');
    setTimeout(() => send.classList.remove('sent'), 450);
  }

  private setBusy(busy: boolean): void {
    this.busy = busy;
    this.els.send.disabled = busy;
  }

  private autosize(): void {
    const { question } = this.els;
    question.style.height = 'auto';
    question.style.height = `${Math.min(question.scrollHeight, 140)}px`;
  }

  private isNearBottom(threshold = 40): boolean {
    const { messages } = this.els;
    return messages.scrollHeight - messages.scrollTop - messages.clientHeight < threshold;
  }

  private scrollToBottom(): void {
    this.els.messages.scrollTop = this.els.messages.scrollHeight;
    this.els.scrollDown.hidden = true;
  }
}
